import * as React from "react";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ExcelStyle, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams, } from "ag-grid-enterprise";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { FilterDef } from "../../../../../constants/FinanceModels";
import { STATE_ACTIVE_FILTERS, STATE_TABLE_UPDATING, } from "../../atoms/OutstandingManagementAtoms";
import { formatCurrency, formatDate, formatDateWithoutTime } from "../../../../../utils/FormatUtils";
import { getLocalGridState, setLocalGridState, } from "../../../../../utils/GridUtils";
import { STATE_SUMMARY_UPDATING } from "../../../../../constants/FinanceRecoil";
import AgGridCustomTable from "../../../../../../../util/component/AgGridCustomTable";
import EmptyState from "../../../../../../../util/component/EmptyState";
import { NumericSummary } from "../../../../../component/NumericSummary";
import ColouredChip from "../../../../../component/ColouredChip";
import * as Const from "../filters/PaymentRequestConstants";
import FinanceDynamicActions from "../../../../../component/FinanceDynamicActions";
import {
    OUTSTANDING_ACCOUNTED,
    OUTSTANDING_ADD_MASSIVE_NOTE,
    OUTSTANDING_DOWNLOAD_XLS,
    OUTSTANDING_EXTERNAL_MANAGEMENT,
    OUTSTANDING_STATUS,
    OUTSTANDING_SUSPEND,
    OUTSTANDING_VIEW_DETAIL
} from "../../../../../utils/FinanceScope";
import { useAgGridLocale, useHasScope } from "../../../../../../../config/globalConfig";
import { Button, IconButton, Tooltip } from "@mui/material";
import { DescriptionRounded, FileDownloadRounded } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { usePaymentRequestRepository } from "../../../../../api/repositories/PaymentRequestRepository";
import dayjs from "dayjs";
import { IsRowSelectable, RowNode, SelectionChangedEvent } from "ag-grid-community";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import CustomModalChangeState from "../../CustomModalChangeState";
import { setSnackbar } from "../../../../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../../../../util/state/globalVariables";
import { GlobalContext } from "../../../../../../../util/state/GlobalContextProvider";
import {ExportDataAsExcelBase} from "../../../../../utils/ExportDataAsExcelBase";
import CustomModalChangeNote from "../../CustomModalChangeNote";

const OutstandingManagementTable = (props: any) => {
    const UNIQUE_ID = "fnn-payments-request";
    const hasScope = useHasScope;
    const { t } = useTranslation("finance", {
        keyPrefix: "payments.payment-request",
    });
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const customerId = queryParams.get('customerId');
    const invoiceNumber = queryParams.get('invoiceNumber');
    const { getPaymentsRequests, getSummaryRequest, putOutstandingByIds, putOutstandingNoteByIds } = usePaymentRequestRepository();
    const navigate = useNavigate();
    const filterDefs = useRecoilValue(STATE_ACTIVE_FILTERS);
    const [updating, setUpdating] = useRecoilState(STATE_TABLE_UPDATING);
    const [items, setItems] = useState<any | undefined>();
    const [selectedItemsInternal, setSelectedItemsInternal] = useState<any | undefined>();
    const [openPutRequest, setOpenPutRequest] = useState(false);
    const [openNoteRequest, setOpenNoteRequest] = useState(false);
    const [statusToTransit, setStatusToTransit] = useState<string>('');
    const [previousStatus, setPreviousStatus] = useState<string>('');
    const { dispatchGlobalData } = useContext(GlobalContext);

    const setSummaryUpdating = useSetRecoilState(STATE_SUMMARY_UPDATING);
    const agGridLocale = useAgGridLocale();

    const gridRef = useRef<AgGridReact>(null);


    const columnDefs: ColDef[] = [
        {
            field: "id",
            headerName: t("table.id"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            checkboxSelection: true,
            headerCheckboxSelection: true,
            lockVisible: true, lockPinned: true,
            showDisabledCheckboxes: true,
            valueFormatter: params => params.value ? params.value : "---"
        },
        {
            field: "createdDate",
            headerName: t("table.creationDate"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: (params: any) => formatDate(params.value),
        },
        {
            field: "modifiedDate",
            headerName: t("table.modificationDate"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: (params: any) => formatDate(params.value),
        },
        {
            field: "lastTransactionDate",
            headerName: t("table.lastTransactionDate"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: (params: any) => formatDate(params.value),
        },
        {
            field: "maskeredPan",
            headerName: t("table.maskeredPan"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: params => params.value ? params.value : "---"
        },
        {
            field: "type",
            headerName: t("table.paymentType"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            lockVisible: true,
            valueFormatter: params => Const.convertTypology(Const.paymentTypes, params.value) ? t(Const.convertTypology(Const.paymentTypes, params.value)) : "---"
        },
        {
            field: "amount",
            headerName: t("table.amount"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            cellClass: "typeString",
            valueFormatter: (params) => formatCurrency(params.value, "EUR", 0.01),
        },
        {
            field: "amountType",
            headerName: t("table.amountType"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            cellRenderer: (params: any) => params.value ? t(Const.convertAmountType(Const.amountTypes, params.value, params.data.usePreAuth)) : "---"
        },
        {
            field: "dueDate",
            headerName: t("table.dueDate"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: (params: any) => formatDateWithoutTime(params.value),
        },
        {
            field: "invoiceNumber",
            headerName: t("table.invoiceNumber"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: params => params.value ? params.value : "---"
        },
        {
            field: "sapInvoiceNumber",
            headerName: t("table.sapInvoiceNumber"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            cellClass: "typeString",
            valueFormatter: params => params.value ? params.value : "---"
        },
        {
            field: "invoiceDate",
            headerName: t("table.invoiceDate"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: (params: any) => formatDateWithoutTime(params.value),
        },
        {
            field: "raCode",
            headerName: t("table.ra"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: params => params.value ? params.value : "---"
        },
        {
            field: "serviceItems",
            headerName: t("table.productType"),
            minWidth: 240,
            unSortIcon: true,
            sortable: true,
            cellClass: "typeString",
            valueFormatter: params => params.value ? params.value : params.data?.correlationName ? params.data.correlationName : t("table.ra")
        },
        {
            field: "raDate",
            headerName: t("table.raDate"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: (params: any) => formatDateWithoutTime(params.value),
        },
        {
            field: "ntwRaData.vehiclePlateCode",
            headerName: t("table.plate"),
            minWidth: 240,
            unSortIcon: true,
            sortable: true,
            cellClass: "typeString",
            valueFormatter: params => params.value ? params.value + "" : "---"
        },
        {
            field: "cardTerminalType",
            headerName: t("table.paymentMode"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: params => params.value ? t(Const.convertTypology(Const.paymentTMode, params.value)) : "---"
        },
        {
            field: "lastTransactionId",
            headerName: t("table.lastTransactionId"),
            minWidth: 240,
            unSortIcon: true,
            sortable: true,
            cellClass: "typeString",
            valueFormatter: params => params.value ? params.value + "" : "---"
        },
        {
            field: "amountRecovered",
            headerName: t("table.amountRecovered"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            cellClass: "typeString",
            valueFormatter: (params) => formatCurrency(params.value, "EUR", 0.01),
        },
        {
            field: "customerId",
            headerName: t("table.customerId"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            cellClass: "typeString",
            valueFormatter: params => params.value ? params.value : "---"
        },
        {
            field: "customerInvoice.firstName",
            headerName: t("table.customerName"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            cellClass: "typeString",
            valueFormatter: params => params?.value ? params.value : (params.data?.customerInvoice ? "---" : params.data.customerName)
        },
        {
            field: "customerInvoice.lastName",
            headerName: t("table.customerLastName"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            cellClass: "typeString",
            valueFormatter: params => params.value ? params.value : "---"
        },
        {
            field: "customerInvoice.companyName",
            headerName: t("table.businessName"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            cellClass: "typeString",
            valueFormatter: params => params.value ? params.value : "---"
        },
        {
            field: "customerRa.firstName",
            headerName: t("table.raOwnerName"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: params => params.value ? params.value : "---"
        },
        {
            field: "customerRa.lastName",
            headerName: t("table.raOwnerLastName"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            cellClass: "typeString",
            valueFormatter: params => params.value ? params.value : "---"
        },
        {
            field: "customerRa.companyName",
            headerName: t("table.raOwnerBusinessName"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            cellClass: "typeString",
            valueFormatter: params => params.value ? params.value : "---"
        },
        {
            field: "customerRa.address",
            headerName: t("table.raOwnerAddress"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            hide: true,
            cellClass: "typeString",
            valueFormatter: params => params.value ? params.value : "---"
        },
        {
            field: "customerRa.email",
            headerName: t("table.raOwnerEmail"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            hide: true,
            cellClass: "typeString",
            valueFormatter: params => params.value ? params.value : "---"
        },
        {
            field: "customerRa.taxCode",
            headerName: t("table.raOwnerFiscalCode"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            hide: true,
            cellClass: "typeString",
            valueFormatter: params => params.value ? params.value : "---"
        },
        {
            field: "customerRa.birthDate",
            headerName: t("table.raOwnerBirthDate"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            hide: true,
            valueFormatter: (params: any) => formatDateWithoutTime(params.value),
        },
        {
            field: "customerRa.birthCity",
            headerName: t("table.raOwnerBirthPlace"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            hide: true,
            cellClass: "typeString",
            valueFormatter: params => params.value ? params.value : "---"
        },
        {
            field: "customerRa.birthProvince",
            headerName: t("table.raOwnerBirthProvince"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            hide: true,
            cellClass: "typeString",
            valueFormatter: params => params.value ? params.value : "---"
        },
        {
            field: "customerRa.birthCountry",
            headerName: t("table.raOwnerNationality"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            hide: true,
            cellClass: "typeString",
            valueFormatter: params => params.value ? params.value : "---"
        },
        {
            field: "customerBooking.firstName",
            headerName: t("table.bookingOwnerName"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: params => params.value ? params.value : "---"
        },
        {
            field: "customerBooking.lastName",
            headerName: t("table.bookingOwnerLastName"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            cellClass: "typeString",
            valueFormatter: params => params.value ? params.value : "---"
        },
        {
            field: "customerBooking.companyName",
            headerName: t("table.bookingOwnerBusinessName"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            cellClass: "typeString",
            valueFormatter: params => params.value ? params.value : "---"
        },

        {
            field: "customerBooking.address",
            headerName: t("table.bookingOwnerAddress"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            hide: true,
            cellClass: "typeString",
            valueFormatter: params => params.value ? params.value : "---"
        },
        {
            field: "customerBooking.email",
            headerName: t("table.bookingOwnerEmail"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            hide: true,
            cellClass: "typeString",
            valueFormatter: params => params.value ? params.value : "---"
        },
        {
            field: "customerBooking.taxCode",
            headerName: t("table.bookingOwnerFiscalCode"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            hide: true,
            cellClass: "typeString",
            valueFormatter: params => params.value ? params.value : "---"
        },
        {
            field: "customerBooking.birthDate",
            headerName: t("table.bookingOwnerBirthDate"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            hide: true,
            valueFormatter: (params: any) => formatDateWithoutTime(params.value),
        },
        {
            field: "customerBooking.birthCity",
            headerName: t("table.bookingOwnerBirthPlace"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            hide: true,
            cellClass: "typeString",
            valueFormatter: params => params.value ? params.value : "---"
        },
        {
            field: "customerBooking.birthProvince",
            headerName: t("table.bookingOwnerBirthProvince"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            hide: true,
            cellClass: "typeString",
            valueFormatter: params => params.value ? params.value : "---"
        },
        {
            field: "customerBooking.birthCountry",
            headerName: t("table.bookingOwnerNationality"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            hide: true,
            cellClass: "typeString",
            valueFormatter: params => params.value ? params.value : "---"
        },
        {
            field: "customerInvoice.province",
            headerName: t("table.countyResidence"),
            minWidth: 240,
            unSortIcon: true,
            sortable: true,
            cellClass: "typeString",
            valueFormatter: params => params.value ? params.value + "" : "---"
        },
        {
            field: "bookingDate",
            headerName: t("table.bookingDate"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            cellClass: "typeString",
            valueFormatter: (params: any) => formatDateWithoutTime(params.value),
        },
        {
            field: "ntwRaData.checkoutCode",
            headerName: t("table.checkoutDmsIdentificationCode"),
            minWidth: 240,
            unSortIcon: true,
            sortable: true,
            cellClass: "typeString",
            valueFormatter: params => params.value ? params.value + "" : "---"
        },
        {
            field: "ntwRaData.checkinCode",
            headerName: t("table.checkinDmsIdentificationCode"),
            minWidth: 240,
            unSortIcon: true,
            sortable: true,
            cellClass: "typeString",
            valueFormatter: params => params.value ? params.value + "" : "---"
        },
        {
            field: "note",
            headerName: t("table.note"),
            minWidth: 240,
            unSortIcon: true,
            sortable: true,
            cellClass: "typeString",
            valueFormatter: params => params.value ? params.value + "" : "---"
        },

        {
          field: "correlationName",
          headerName: t("table.correlationName"),
          minWidth: 200,
          unSortIcon: true,
          sortable: true,
          cellRenderer: (params: any) => Const.checkCorrelationName(params, t)
        },
        {
          field: "correlationId",
          headerName: t("table.correlationId"),
          minWidth: 200,
          unSortIcon: true,
          sortable: true,
          valueFormatter: params => Const.checkCorrelationID(params, t)
        },
        {
            field: "customerInvoice.mobileNumber",
            headerName: t("table.mobileNumber"),
            minWidth: 240,
            unSortIcon: true,
            sortable: true,
            cellClass: "typeString",
            hide: true,
            valueFormatter: params => params.value ? params.value + "" : "---"
        },
    {
      field: "status",
            headerName: t("table.status"),
            minWidth: 150,
            unSortIcon: true,
            sortable: true,
            pinned: "right",
            lockPosition: "right",
            lockVisible: true,
            lockPinned: true,
            cellRendererSelector: (params) => {
                let status: string = params.data.status;
                status = params.data?.preauthIsReleased ? 'RELEASED'
                        : params.data?.amountType === "PRE_AUTH" ? 'OPENED' : status
                let color = Const.statuses
                        .find((s) => s?.value === status)
                        ?.color?.split(".")[0];
                if (color === "neutral") color = "default";
                if (status === "OK") status = "SUCCESS";
                return {
                    component: () => <ColouredChip name={status} color={color}/>,
                };
            },
        },
        {
            field: "legalEntity",
            headerName: t("table.legalEntity"),
            minWidth: 180,
            unSortIcon: true,
            sortable: true,
        },
        {
            field: "dealer",
            headerName: t("table.dealer"),
            minWidth: 180,
            unSortIcon: true,
            sortable: true,
        },
        {
            field: "reason",
            headerName: t("table.reason"),
            minWidth: 240,
            unSortIcon: true,
            sortable: true,
            cellClass: "typeString",
            hide: true,
            valueFormatter: params => params.value ? params.value + "" : "---"
        },
        {
            field: '_actions', headerName: '',
            suppressSizeToFit: true, minWidth: 100, maxWidth: 110, sortable: false,
            pinned: 'right', lockPosition: 'right', lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                return {
                    component: () => {
                        if (!hasScope(OUTSTANDING_VIEW_DETAIL)) {
                            return null;
                        }

                        return (
                                <FinanceDynamicActions>
                                    <Tooltip arrow placement={"top"} title={t("table.actions.detail")}>
                                        <IconButton color="accent" onClick={() => {
                                            window.open(`/finance/payments/outstanding-management/${params.data.id}`, "_blank")
                                        }}>
                                            <DescriptionRounded/>
                                        </IconButton>
                                    </Tooltip>
                                </FinanceDynamicActions>
                        );
                    },
                };
            },
            suppressFiltersToolPanel: true
        }
    ];


    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data["id"],
        suppressRowClickSelection: true,
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false
        },
        noRowsOverlayComponentParams: {
            title: t("table.no-rows.title"),
            content: t("table.no-rows.content")
        }

    };

    useEffect(() => {
        gridRef.current?.api?.setServerSideDatasource(
                createDatasource(filterDefs, gridRef.current.api)
        );
        setSummaryUpdating(true);
    }, [filterDefs]);

    useEffect(() => {
        if (updating) {
            gridRef.current?.api?.setServerSideDatasource(
                    createDatasource(filterDefs, gridRef.current.api)
            );
            setUpdating(false);
            setSummaryUpdating(true);
        }
    }, [updating]);

    useEffect(() => {
        setSummaryUpdating(true);
    }, [agGridLocale]);

    const createDatasource = (filterDefs: FilterDef[], api: GridApi<any>) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                let sort =
                        params.request.sortModel.length === 0
                                ? null
                                : params.request.sortModel[0].colId;
                let order =
                        params.request.sortModel.length === 0
                                ? null
                                : params.request.sortModel[0].sort.toUpperCase();
                getPaymentsRequests(startRow, endRow - startRow, sort, order, filterDefs, customerId, invoiceNumber, null, null,null,true)
                        .then((response) => {
                            let result = response.result;
                            if (result["total-amount"] === 0) {
                                api.showNoRowsOverlay();
                            } else {
                                api.hideOverlay();
                            }
                            setItems(result.content)
                            params.success({
                                rowData: result.content,
                                rowCount: result["total-amount"],
                            });
                        })
                        .catch(() => params.fail());
            },
        };
    };

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    const toolbar = {
        renderInfo: () => (
                <NumericSummary
                        query={() => getSummaryRequest(filterDefs, true)}
                        fields={Const.getOutstandingSummaryFields()}
                        i18nPath={"payments.payment-request.statuses"}
                />
        ),
        renderActions: () => (
                <>
                    {hasScope(OUTSTANDING_ADD_MASSIVE_NOTE) &&
                        <Button fullWidth color={"accent"} variant="contained" size={"small"}
                                disabled={!(!!selectedItemsInternal) || (selectedItemsInternal && selectedItemsInternal.length === 0)}
                                onClick={() => {
                                    setOpenNoteRequest(true)
                                }}>
                            {t('pages.wizard.buttons.update-notes')}
                        </Button>
                    }
                    {hasScope(OUTSTANDING_STATUS) &&
                            <Button fullWidth color={"accent"} variant="contained" size={"small"}
                                    disabled={!showActions(Const.ID_FILTER_OUTSTANDING) || !(!!selectedItemsInternal) || (selectedItemsInternal && selectedItemsInternal.length === 0)}
                                    onClick={() => {
                                        setOpenPutRequest(true)
                                        setStatusToTransit('outstanding')
                                    }}>
                                {t('pages.wizard.buttons.outstanding')}
                            </Button>}
                    {hasScope(OUTSTANDING_SUSPEND) &&
                            <Button fullWidth color={"accent"} variant="contained" size={"small"}
                                    disabled={!showActions(Const.ID_FILTER_SUSPENDED) || !(!!selectedItemsInternal) || (selectedItemsInternal && selectedItemsInternal.length === 0)}
                                    onClick={() => {
                                        setOpenPutRequest(true)
                                        setStatusToTransit('suspended')
                                    }}>
                                {t('pages.wizard.buttons.suspend')}
                            </Button>}
                    {hasScope(OUTSTANDING_ACCOUNTED) &&
                            <Button fullWidth color={"accent"} variant="contained" size={"small"}
                                    disabled={!showActions(Const.ID_FILTER_ACCOUNTED) || !(!!selectedItemsInternal) || (selectedItemsInternal && selectedItemsInternal.length === 0)}
                                    onClick={() => {
                                        setOpenPutRequest(true)
                                        setStatusToTransit('accounted')
                                    }}>
                                {t('pages.wizard.buttons.close')}
                            </Button>
                    }
                    {hasScope(OUTSTANDING_EXTERNAL_MANAGEMENT) &&
                            <Button fullWidth color={"accent"} variant="contained" size={"small"}
                                    disabled={!showActions(Const.ID_FILTER_EXTERNAL_MANAGEMENT) || !(!!selectedItemsInternal) || (selectedItemsInternal && selectedItemsInternal.length === 0)}
                                    onClick={() => {
                                        setOpenPutRequest(true)
                                        setStatusToTransit('external-management')
                                    }}>
                                {t('pages.wizard.buttons.external-management')}
                            </Button>}
                    {hasScope(OUTSTANDING_DOWNLOAD_XLS) &&
                            <Button fullWidth color={"accent"} variant="contained" component="label" size={"small"} onClick={exportAsExcel}>
                                <FileDownloadRounded/> {t('pages.wizard.buttons.download')}
                            </Button>
                    }

                </>
        )
    };

    const showActions = (status: string) => {
        switch (status) {
            case Const.ID_FILTER_OUTSTANDING:
                return previousStatus === Const.ID_FILTER_SUSPENDED || previousStatus === Const.ID_FILTER_EXTERNAL_MANAGEMENT || previousStatus === Const.ID_FILTER_ACCOUNTED;
            case Const.ID_FILTER_SUSPENDED:
                return previousStatus === Const.ID_FILTER_OUTSTANDING || previousStatus === Const.ID_FILTER_EXTERNAL_MANAGEMENT;
            case Const.ID_FILTER_EXTERNAL_MANAGEMENT:
                return previousStatus === Const.ID_FILTER_OUTSTANDING || previousStatus === Const.ID_FILTER_SUSPENDED || previousStatus === Const.ID_FILTER_ACCOUNTED;
            case Const.ID_FILTER_ACCOUNTED:
                return previousStatus === Const.ID_FILTER_OUTSTANDING || previousStatus === Const.ID_FILTER_SUSPENDED || previousStatus === Const.ID_FILTER_EXTERNAL_MANAGEMENT;
        }
    }

    function saveState() {
        setLocalGridState(UNIQUE_ID, {
            rows: gridRef.current!.api.paginationGetPageSize(),
            cols: gridRef.current!.columnApi.getColumnState(),
        });
    }

    function getExportableExcelColumns(): string[] {
        let keys: string[] = gridRef?.current?.columnApi.getAllGridColumns().map(column => column.getColId()) as string[];
        let amountIndex: number = keys.findIndex(column => column === 'status');

        return keys;
    }

    function exportAsExcel() {
        gridRef.current!.api.deselectAll();
        gridRef?.current?.api.exportDataAsExcel({
            ...ExportDataAsExcelBase,
            author: "Finance - Payments",
            fileName: `PaymentRequest - ${dayjs().format("L")}.xlsx`,
            sheetName: `PaymentRequest`,
            columnKeys: getExportableExcelColumns(),
            processCellCallback: (params) => {
                if (params.column.getColId() === 'amount' || params.column.getColId() === 'preauthAmountReleased') {
                    if (params.value) {
                        const numericValue = parseFloat(params.value) / 100;
                        return numericValue.toFixed(2) + " €";
                    } else {
                        return "";
                    }
                }
                if (params.column.getColId() === 'amountType') {
                    return t(Const.convertTypology(Const.amountTypes, params.value));
                }
                if (params.column.getColId() === 'type' && params?.node?.data?.type) {
                    return Const.convertTypology(Const.paymentTypes, params.value) ? t(Const.convertTypology(Const.paymentTypes, params.value)) : "---";
                }
                if (params.column.getColId() === 'invoiceDate' || params.column.getColId() === 'dueDate'
                        || params.column.getColId() === 'raDate' || params.column.getColId() === 'bookingDate'
                        || params.column.getColId() === 'customerBooking.birthDate' || params.column.getColId() === 'customerRa.birthDate') {
                    return formatDateWithoutTime(params.value);
                }
                if (params.column.getColId() === 'lastTransactionDate' || params.column.getColId() === 'modifiedDate' || params.column.getColId() === 'createdDate') {
                    return formatDate(params.value);
                }

                let raCode = params.node?.data?.raCode;
                if (raCode && params.column.getColId() === 'correlationName'){
                    return "RA";
                }
                if (raCode && params.column.getColId() === 'correlationId'){
                    return raCode;
                } else if (!raCode) {
                    return params.value;
                }

                if (params.column.getColId() === 'status') {
                    let status: string = params.value;
                    status = params.node?.data?.preauthIsReleased ? 'RELEASED'
                            : params.node?.data?.amountType === "PRE_AUTH" ? 'OPENED' : status;
                    return t("statuses." + status);
                }

                return params.value;
            },
        });
    }

    const excelStyles: ExcelStyle[] = [
        {
            id: "typeString",
            dataType: "String"
        }
    ];

    const isRowSelectable = useMemo<IsRowSelectable>(() => {
        return (params: RowNode<any>) => {
            return !!params.data;
        };
    }, []);
    const onSelectionChanged = (event: SelectionChangedEvent) => {
        let selectedRows = gridRef.current!.api.getSelectedRows();
        if (selectedRows.length > 0) {
            if (selectedRows.map(s => s.status).every((item, index, array) => item === array[0])) {
                setSelectedItemsInternal(selectedRows.map(s => s.id));
                setPreviousStatus(selectedRows[0].status)
            } else {
                setSelectedItemsInternal(false);
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('snackbar.selectPaymentRequestSameState'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true
                        }
                    }
                });
            }
        } else {
            setSelectedItemsInternal(false);
        }
    };

    useEffect(() => {
        setSelectedItemsInternal(props.selectedItems)
    }, [props.selectedItems]);

    function putRequest() {
        putOutstandingByIds(selectedItemsInternal, statusToTransit).then(() => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('snackbar.success-change-state'),
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true
                    }
                }
            });
            gridRef.current!.api.deselectAll();
            setSelectedItemsInternal(false);
            setUpdating(true);
        }).catch(() => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('snackbar.error-change-state'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });
        });
    }

    function putNoteRequest(note: any) {
        const payload = {
            ids: selectedItemsInternal,
            note: note,
        };
        putOutstandingNoteByIds(payload).then(() => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('snackbar.success-change-state'),
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true
                    }
                }
            });
            gridRef.current!.api.deselectAll();
            setSelectedItemsInternal(false);
            setUpdating(true);
        }).catch(() => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('snackbar.error-change-state'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });
        });
    }

    return (
            <>
                <AgGridCustomTable
                        toolbar={toolbar}
                        rowModelType="serverSide"
                        paginationPageSize={getLocalGridState(UNIQUE_ID).rows}
                        serverSideInfiniteScroll={true}
                        gridRef={gridRef}
                        columnDefs={columnDefs}
                        excelStyles={excelStyles}
                        gridOptions={gridOptions}
                        rowSelection={"multiple"}
                        rowData={items}
                        onSelectionChanged={onSelectionChanged}
                        isRowSelectable={isRowSelectable}
                        noRowsOverlayComponent={noRowsOverlayComponent}
                        onGridReady={() => {
                            gridRef.current!.api.setServerSideDatasource(
                                    createDatasource(filterDefs, gridRef.current!.api)
                            );
                            let gridState = getLocalGridState(UNIQUE_ID).cols;
                            if (gridState !== undefined) {
                                gridRef.current!.columnApi.applyColumnState({
                                    state: gridState,
                                    applyOrder: true,
                                });
                            }
                        }}
                        onColumnMoved={(e) => {
                            if (e.source === "api") return;
                            saveState();
                        }}
                        onColumnPinned={(e) => {
                            if (e.source === "api") return;
                            saveState();
                        }}
                        onColumnResized={(e) => {
                            if (e.source === "api") return;
                            saveState();
                        }}
                        onSortChanged={(e) => {
                            if (e.source === "api") return;
                            saveState();
                        }}
                        onPaginationChanged={() => {
                            if (gridRef.current!.api) {
                                setLocalGridState(UNIQUE_ID, {
                                    rows: gridRef.current!.api?.paginationGetPageSize(),
                                    cols: getLocalGridState(UNIQUE_ID).cols,
                                });
                            }
                        }}
                ></AgGridCustomTable>

                <CustomModalChangeState
                        open={openPutRequest} title={t('modal.sendConfirmation')}
                        content={t('modal.sendConfirmationMsg', { n: selectedItemsInternal ? selectedItemsInternal.length : 0 })}
                        cancelButton={t('modal.actionButtons.cancel')}
                        saveButton={t('modal.actionButtons.confirm')}
                        titleIcon={<InfoRoundedIcon/>}
                        onSave={() => {
                            setOpenPutRequest(false);
                            putRequest();
                        }}
                        onClose={() => {
                            setOpenPutRequest(false);
                        }}/>

                <CustomModalChangeNote
                    open={openNoteRequest} title={t('modal.sendNoteConfirmation')}
                    content={t('modal.sendNoteConfirmationMsg', { n: selectedItemsInternal ? selectedItemsInternal.length : 0 })}
                    cancelButton={t('modal.actionButtons.cancel')}
                    saveButton={t('modal.actionButtons.confirm')}
                    contentNote={t('modal.contentNoteMsg')}
                    titleIcon={<InfoRoundedIcon/>}
                    onSave={(note: any) => {
                        setOpenNoteRequest(false);
                        putNoteRequest(note);
                    }}
                    onClose={() => {
                        setOpenNoteRequest(false);
                    }}/>
            </>
    );
};

export default OutstandingManagementTable;


